// The Follow-ups section of the Sentinel Workspace, laid out like the ticket view: the follow-ups
// as a list on the left, the ticket's messages in the middle, and the follow-up beside them.

import { useEffect, useMemo, useState } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { queryClientAtom } from 'jotai-tanstack-query';
import { PanelLeft, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { TicketList, type TicketListItem } from '@/components/shared/ticket-list';
import { threadFromWire } from '@/adapters/sentinelWorkspace.conversation.adapter';
import { ticketListCollapsedAtom } from '@/hooks/use-sentinel-workspace';
import { cn, getApiErrorMessage } from '@/lib/utils';
import { StatTile } from '../stat-tile';
import { ChatThread, ConversationHeader } from '../chat-thread';
import { ListIconButton } from '../list-icon-button';
import { FOLLOWUP_REASON_LABELS, FOLLOWUP_VIEW_LABELS, followupStateText } from './followup-copy';
import { followupEventText } from './followup-format';
import { FollowupDetailsBody } from './followup-drawer';
import { TicketContextPanel } from '../ticket-context-panel';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useIsMobile } from '@/hooks/use-mobile';
import { FollowupRowItem } from './followup-row';
import { FollowupComposer } from './followup-composer';
import { canSendFollowup } from './followup-format';
import {
  followupCountsQueryAtom,
  followupDetailQueryAtom,
  followupFiltersAtom,
  followupListQueryAtom,
  followupReportQueryAtom,
  followupViewAtom,
  followupWorkflowsAtom,
  openFollowupIdAtom,
} from './followup-state';
import { sentinelViewCountsAtom } from '@/hooks/use-sentinel-routing';
import { TicketStatusFilter } from '../ticket-status-filter';
import { MENU_ITEM, MENU_LABEL_ROW, MENU_META, MENU_PANEL, MENU_SECTION, MENU_SUB_TRIGGER } from '../workspace-menu-styles';
import { MenuCheck, MenuChevron, MenuDivider } from '../workspace-menu-icons';
import { FOLLOWUP_VIEWS, type FollowupRow, type FollowupView } from './types';
import { useBrandTimeZone } from '@/hooks/use-ai-settings';

// Radix Select has no empty value, so "any" is a value of its own.
const ANY = 'any';

/** The reasons the Reason submenu offers, in the design's order (Figma 1070:47940): the holds, then the ends. */
const FIGMA_REASONS = [
  'outside_window',
  'handoff_open',
  'recent_automated_message',
  'other_ticket_reply',
  'customer_reply',
  'human_reply',
  'closed',
  'assigned',
] as const;

function StatsStrip() {
  const report = useAtomValue(followupReportQueryAtom);
  if (report.isError) return null;
  const counts = report.data?.counts;
  const tiles: [string, number | undefined][] = [
    ['Scheduled', counts?.scheduled],
    ['Sent', counts?.sent],
    ['Cancelled', counts?.cancelled],
    ['Prevented duplicates', counts?.preventedDuplicates],
  ];
  return (
    <section aria-label="Follow-ups in the last 7 days" className="flex flex-col gap-1.5">
      <p className="text-[12px] font-medium text-gray-700">Last 7 days</p>
      <div className="flex flex-wrap justify-center gap-2">
        {tiles.map(([label, value]) => (
          <StatTile key={label} label={label} value={value === undefined ? '–' : String(value)} />
        ))}
      </div>
    </section>
  );
}

/** The ticket's messages, as the ticket view shows them; they come with the follow-up's details. */
function FollowupThread({ row }: { row: FollowupRow }) {
  const query = useAtomValue(followupDetailQueryAtom);
  const timeZone = useBrandTimeZone();
  const detail = query.data?.wait.id === row.id ? query.data : null;
  const messages = useMemo(
    () => (detail ? threadFromWire(detail.messages, row.sentinelTicketId, row.provider === 'helpdesk') : []),
    [detail, row.sentinelTicketId, row.provider],
  );
  if (query.isError) {
    return (
      <p role="alert" className="p-6 text-center text-[13px] text-red-600">
        {getApiErrorMessage(query.error, 'Could not load the messages.')}
      </p>
    );
  }
  if (!detail) {
    return (
      <div className="flex flex-col gap-4 p-4">
        <Skeleton className="h-20 w-2/3 rounded-2xl" />
        <Skeleton className="ml-auto h-20 w-2/3 rounded-2xl" />
      </div>
    );
  }
  if (messages.length === 0) return <p className="p-6 text-center text-[13px] text-gray-800">No messages on this ticket yet.</p>;
  return <ChatThread messages={messages} timeZone={timeZone} />;
}

type FilterOption = { value: string; label: string; meta?: string | number };

/** One choice in a follow-up menu (Figma Menu Item): the chosen one has a medium label and a check. */
function FilterRows({ options, value, onSelect }: { options: FilterOption[]; value: string; onSelect: (value: string) => void }) {
  return options.map((option) => {
    const selected = option.value === value;
    return (
      <DropdownMenuItem
        key={option.value}
        role="menuitemradio"
        aria-checked={selected}
        onSelect={() => onSelect(option.value)}
        className={MENU_ITEM}
      >
        <span className={MENU_LABEL_ROW}>
          <span className={cn('truncate', selected ? 'font-medium' : 'font-normal')}>{option.label}</span>
          {option.meta !== undefined && <span className={MENU_META}>{option.meta}</span>}
        </span>
        {selected && <MenuCheck />}
      </DropdownMenuItem>
    );
  });
}

/**
 * The Follow up action submenu of the view switch (Figma 1070:47674): the view with its count, then
 * workflow and reason. Picking any of them opens Follow-ups with that choice.
 */
export function FollowupViewMenu({ onPick }: { onPick: () => void }) {
  const [view, setView] = useAtom(followupViewAtom);
  const [filters, setFilters] = useAtom(followupFiltersAtom);
  const counts = useAtomValue(followupCountsQueryAtom).data;
  const workflows = useAtomValue(followupWorkflowsAtom);
  const workflowName = filters.workflowId ? (workflows.get(filters.workflowId) ?? 'One workflow') : null;
  const reasonName = filters.reason ? (FOLLOWUP_REASON_LABELS[filters.reason] ?? filters.reason) : null;
  return (
    <>
      <DropdownMenuLabel className={MENU_SECTION}>View</DropdownMenuLabel>
      <FilterRows
        value={view}
        onSelect={(value) => {
          setView(value as FollowupView);
          onPick();
        }}
        options={FOLLOWUP_VIEWS.map((key) => ({ value: key, label: FOLLOWUP_VIEW_LABELS[key], meta: counts ? (counts[key] ?? 0) : undefined }))}
      />
      <MenuDivider />
      <DropdownMenuSub>
        <DropdownMenuSubTrigger className={MENU_SUB_TRIGGER}>
          <span className={MENU_LABEL_ROW}>
            <span>Workflow</span>
            <span className={cn(MENU_META, 'max-w-[110px] truncate')}>{workflowName ?? 'All'}</span>
          </span>
          <MenuChevron />
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent sideOffset={6} className={cn(MENU_PANEL, 'max-h-[360px] w-[240px] min-w-0 overflow-y-auto')}>
          <FilterRows
            value={filters.workflowId ?? ANY}
            onSelect={(value) => {
              setFilters({ ...filters, workflowId: value === ANY ? null : value });
              onPick();
            }}
            options={[{ value: ANY, label: 'All workflows' }, ...[...workflows].map(([id, name]) => ({ value: id, label: name }))]}
          />
        </DropdownMenuSubContent>
      </DropdownMenuSub>
      <DropdownMenuSub>
        <DropdownMenuSubTrigger className={MENU_SUB_TRIGGER}>
          <span className={MENU_LABEL_ROW}>
            <span>Reason</span>
            <span className={cn(MENU_META, 'max-w-[110px] truncate')}>{reasonName ?? 'Any'}</span>
          </span>
          <MenuChevron />
        </DropdownMenuSubTrigger>
        {/* Figma 1070:47940: 323px wide. */}
        <DropdownMenuSubContent sideOffset={6} className={cn(MENU_PANEL, 'w-[323px] min-w-0')}>
          <FilterRows
            value={filters.reason ?? ANY}
            onSelect={(value) => {
              setFilters({ ...filters, reason: value === ANY ? null : value });
              onPick();
            }}
            options={[
              { value: ANY, label: 'Any reason' },
              ...FIGMA_REASONS.map((code) => ({ value: code, label: FOLLOWUP_REASON_LABELS[code] ?? code })),
            ]}
          />
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    </>
  );
}

/**
 * The right column: the follow-up, and the ticket's own details as the ticket view shows them, as two tabs of one column so the workspace never needs a fourth.
 */
function FollowupSide({ row, canEdit, onChanged }: { row: FollowupRow; canEdit: boolean; onChanged: () => void }) {
  const [tab, setTab] = useState<'followup' | 'ticket'>('followup');
  const conversationId = row.conversationId;
  return (
    <aside aria-label="Follow-up" className="flex w-[360px] shrink-0 flex-col border-l border-gray-500 bg-white">
      {conversationId !== null && (
        <Tabs value={tab} onValueChange={(value) => setTab(value as 'followup' | 'ticket')} className="shrink-0 px-4 pt-3">
          <TabsList aria-label="Follow-up or ticket details" className="w-full">
            <TabsTrigger value="followup" className="flex-1">
              Follow-up
            </TabsTrigger>
            <TabsTrigger value="ticket" className="flex-1">
              Ticket details
            </TabsTrigger>
          </TabsList>
        </Tabs>
      )}
      {/* Both stay mounted and only one shows, so a draft being edited survives a look at the ticket. */}
      {tab === 'ticket' && conversationId !== null && (
        <div className="flex min-h-0 flex-1 [&>*]:w-full! [&>*]:min-w-0! [&>*]:border-l-0">
          <TicketContextPanel conversationId={conversationId} onCollapse={() => setTab('followup')} />
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-y-auto" hidden={tab === 'ticket' && conversationId !== null}>
        <ul aria-label="Follow-up status" className="p-4 pb-0">
          <FollowupRowItem row={row} canEdit={canEdit} onOpen={() => {}} onChanged={onChanged} showDetails={false} showDraft={false} />
        </ul>
        <FollowupDetailsBody onChanged={onChanged} />
      </div>
    </aside>
  );
}

/** `viewSwitch`: the workspace's view picker (My tickets / Review / Follow-ups), which is also the way back; it names the page. */
export function FollowupsPanel({ canEdit, viewSwitch }: { canEdit: boolean; viewSwitch: React.ReactNode }) {
  const [filters, setFilters] = useAtom(followupFiltersAtom);
  const list = useAtomValue(followupListQueryAtom);
  const queryClient = useAtomValue(queryClientAtom);
  const [openId, setOpenId] = useAtom(openFollowupIdAtom);
  const [listCollapsed, setListCollapsed] = useAtom(ticketListCollapsedAtom);
  // Phones show one pane at a time: the list, or the open follow-up as one column with a way back.
  const isMobile = useIsMobile();
  const [search, setSearch] = useState(filters.search);

  // Typing does not refetch per keystroke; the filter settles a moment after the last one.
  useEffect(() => {
    const timer = setTimeout(() => setFilters((current) => (current.search === search ? current : { ...current, search })), 300);
    return () => clearTimeout(timer);
  }, [search, setFilters]);

  const rows = useMemo(() => list.data?.pages.flatMap((page) => page.items) ?? [], [list.data]);
  const statusCounts = useAtomValue(sentinelViewCountsAtom)?.followup;
  const selected = rows.find((row) => row.id === openId) ?? null;

  // Leaving the view closes the follow-up, or the ticket view would open it again in its drawer.
  useEffect(() => () => setOpenId(null), [setOpenId]);

  // Workflow options come from the rows; seen ones are kept so the chosen option never vanishes under a filter.
  const setWorkflows = useSetAtom(followupWorkflowsAtom);
  useEffect(() => {
    setWorkflows((known) => {
      const fresh = rows.filter((row) => row.workflow.id && !known.has(row.workflow.id));
      if (fresh.length === 0) return known;
      const next = new Map(known);
      for (const row of fresh) next.set(row.workflow.id!, row.workflow.name ?? 'Unnamed workflow');
      return next;
    });
  }, [rows, setWorkflows]);

  const items = useMemo<TicketListItem<string>[]>(
    () =>
      rows.map((row) => ({
        id: row.id,
        subject: `#${row.ticketId}`,
        customerName: row.customer.name ?? row.customer.email,
        preview: row.lastEvent ? followupEventText(row.lastEvent.text) : null,
        category: followupStateText(row),
        timestamp: row.lastEvent?.at ?? row.updatedAt,
      })),
    [rows],
  );

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['sentinel', 'followups'] });

  return (
    <div className="flex h-full w-full overflow-hidden bg-white">
      {(isMobile ? selected === null : !listCollapsed) && (
        <TicketList<string>
          className={isMobile ? 'w-full' : undefined}
          items={items}
          title={viewSwitch}
          isLoading={list.isPending}
          selectedId={openId}
          onSelect={setOpenId}
          search={{ value: search, onChange: setSearch, placeholder: 'Search by ticket ID' }}
          searchAction={<TicketStatusFilter counts={statusCounts} />}
          // The error shows in the toolbar, rows or not: a failed refresh must not pass stale rows off as current.
          emptyMessage={list.isError ? '' : 'No follow-ups in this view.'}
          paging={{ hasMore: list.hasNextPage, isFetchingNextPage: list.isFetchingNextPage, onLoadMore: () => void list.fetchNextPage() }}
          headerActions={
            <ListIconButton label="Refresh follow-ups" onClick={refresh} disabled={list.isFetching}>
              <RefreshCw className={list.isFetching ? 'size-[14px] animate-spin' : 'size-[14px]'} />
            </ListIconButton>
          }
          toolbar={
            list.isError && (
              <div role="alert" className="flex items-center justify-between gap-2 text-[12px] text-red-600">
                <span>{getApiErrorMessage(list.error, 'Could not load follow-ups.')}</span>
                <Button variant="outline" size="sm" onClick={() => void list.refetch()}>
                  Retry
                </Button>
              </div>
            )
          }
          onCollapse={() => setListCollapsed(true)}
        />
      )}

      {selected && isMobile ? (
        // One column: the follow-up first (its draft and Send are what an urgent visit is for), then
        // the ticket's messages, then the rest of the details.
        <div key={selected.id} className="flex min-w-0 flex-1 flex-col">
          <ConversationHeader
            displayId={selected.ticketId}
            listCollapsed
            onExpandList={() => setOpenId(null)}
            detailsCollapsed={false}
            onExpandDetails={() => {}}
          />
          <div className="min-h-0 flex-1 overflow-y-auto bg-gray-100 pb-4">
            <ul aria-label="Follow-up status" className="p-3">
              <FollowupRowItem row={selected} canEdit={canEdit} onOpen={() => {}} onChanged={refresh} showDetails={false} showDraft={false} />
            </ul>
            <FollowupThread row={selected} />
            {selected.state === 'queued' && selected.draft && (
              <div className="p-3">
                <FollowupComposer key={selected.draft.receivedAt} row={selected} canSend={canSendFollowup(selected, canEdit)} onChanged={refresh} />
              </div>
            )}
            <div className="mt-4 bg-white">
              <FollowupDetailsBody onChanged={refresh} />
            </div>
          </div>
        </div>
      ) : selected ? (
        <>
          <div className="flex min-w-[420px] flex-1 flex-col">
            <ConversationHeader
              displayId={selected.ticketId}
              listCollapsed={listCollapsed}
              onExpandList={() => setListCollapsed(false)}
              detailsCollapsed={false}
              onExpandDetails={() => {}}
            />
            <div className="min-h-0 flex-1 overflow-y-auto bg-gray-100 pb-4">
              <FollowupThread row={selected} />
            </div>
            {/* Kim's draft under the messages, as the ticket view shows a suggested reply; sent from here. */}
            {selected.state === 'queued' && selected.draft && (
              <div className="max-h-[60%] shrink-0 overflow-y-auto bg-gray-100 p-4 pt-0">
                <FollowupComposer
                  key={`${selected.id}:${selected.draft.receivedAt}`}
                  row={selected}
                  canSend={canSendFollowup(selected, canEdit)}
                  onChanged={refresh}
                />
              </div>
            )}
          </div>
          {/* Keyed: switching follow-ups must not carry a half-done Cancel or opt-out over to the next one. */}
          <FollowupSide key={selected.id} row={selected} canEdit={canEdit} onChanged={refresh} />
        </>
      ) : isMobile ? null : (
        <div className="relative flex h-full flex-1 flex-col items-center justify-center gap-4 bg-gray-100 p-6">
          {listCollapsed && (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => setListCollapsed(false)}
              className="absolute left-4 top-4 text-gray-900"
              aria-label="Expand ticket list"
            >
              <PanelLeft className="size-[14px]" />
            </Button>
          )}
          <p className="text-[13px] leading-[18px] text-text-body">Choose a follow-up to see its ticket and what happens next</p>
          <StatsStrip />
        </div>
      )}
    </div>
  );
}
